import { supabase } from './supabase.js'

/**
 * Free-form tags, shared across payables, builds, accounts, plans, income
 * sources and notes. A tag is created once and reused everywhere, so the same
 * word never ends up meaning two things.
 *
 * Colours come from the app's own palette rather than a picker — a tag should
 * take one tap to make, and nothing here is improved by letting him choose
 * from sixteen million.
 */

export const TAG_COLORS = [
  '#0B6E67', '#4E7A3A', '#A8482A', '#4A5686', '#7A5C8E', '#8A6D14', '#B3321C',
]

export const TAGGABLE = [
  'payable', 'build', 'account', 'installment_plan', 'income_source', 'entry',
]

export async function getTags() {
  const { data, error } = await supabase.from('tags').select('*').order('name')
  if (error) throw error
  return data ?? []
}

/** Reuses an existing tag of the same name rather than making a near-duplicate. */
export async function createTag(name, color) {
  const clean = name.trim()
  const { data: existing } = await supabase
    .from('tags').select('*').ilike('name', clean).maybeSingle()
  if (existing) return existing

  const { data, error } = await supabase
    .from('tags')
    .insert({ name: clean, color: color || TAG_COLORS[0] })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function getTagsFor(targetType, targetId) {
  const { data, error } = await supabase
    .from('taggings')
    .select('id, tag_id')
    .eq('target_type', targetType)
    .eq('target_id', targetId)
  if (error) throw error
  return data ?? []
}

/** One query for a whole list, so a screen of rows is not a query per row. */
export async function getTagsForMany(targetType, ids) {
  if (!ids.length) return {}
  const { data, error } = await supabase
    .from('taggings')
    .select('tag_id, target_id')
    .eq('target_type', targetType)
    .in('target_id', ids)
  if (error) throw error

  const out = {}
  for (const t of data ?? []) (out[t.target_id] ||= []).push(t.tag_id)
  return out
}

export async function tag(tagId, targetType, targetId) {
  const { error } = await supabase
    .from('taggings')
    .upsert(
      { tag_id: tagId, target_type: targetType, target_id: targetId },
      { onConflict: 'tag_id,target_type,target_id' },
    )
  if (error) throw error
}

export async function untag(taggingId) {
  const { error } = await supabase.from('taggings').delete().eq('id', taggingId)
  if (error) throw error
}

/**
 * Removes the tag itself, everywhere. Taggings cascade, so this is the one
 * genuinely destructive action here and the caller has to mean it.
 */
export async function deleteTag(id) {
  const { error } = await supabase.from('tags').delete().eq('id', id)
  if (error) throw error
}
